/**
 * Partner Projects Page
 * Manage GCP projects for automated billing
 */

import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc, deleteDoc, orderBy } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../services/firebase';
import { useAuthStore } from '../store/authStore';
import PartnerProjectModal from '../components/PartnerProjectModal';
import './PartnerProjects.css';

// Admin emails that can see all data
const ADMIN_EMAILS = [
  'michelle.armirola@gmail.com',
  'roseller@fusionict.com',
];

const checkIsAdmin = (email) => {
  if (!email) return false;
  return ADMIN_EMAILS.includes(email.toLowerCase());
};

const PartnerProjects = () => {
  const { user } = useAuthStore();
  const isAdmin = checkIsAdmin(user?.email);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [syncing, setSyncing] = useState({});
  const [expandedProject, setExpandedProject] = useState(null);
  const [error, setError] = useState(null);

  const functions = getFunctions();

  useEffect(() => {
    if (!user) return;

    // Subscribe to gcpProjects - admins see all, regular users see their own
    const projectsQuery = isAdmin
      ? query(collection(db, 'gcpProjects'))
      : query(collection(db, 'gcpProjects'), where('userId', '==', user.uid));

    const unsubProjects = onSnapshot(projectsQuery, (snapshot) => {
      const projectsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      // Sort client-side for admins
      if (isAdmin) {
        projectsData.sort((a, b) => (a.gcpProjectName || '').localeCompare(b.gcpProjectName || ''));
      }
      setProjects(projectsData);
      setLoading(false);
    }, (err) => {
      console.error('Error fetching projects:', err);
      setError(err.message);
      setLoading(false);
    });

    // Subscribe to clients for reference - admins see all
    const clientsQuery = isAdmin
      ? query(collection(db, 'clients'))
      : query(collection(db, 'clients'), where('userId', '==', user.uid));

    const unsubClients = onSnapshot(clientsQuery, (snapshot) => {
      const clientsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      // Sort client-side for admins
      if (isAdmin) {
        clientsData.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      }
      setClients(clientsData);
    });

    return () => {
      unsubProjects();
      unsubClients();
    };
  }, [user, isAdmin]);

  const handleAdd = () => {
    setEditingProject(null);
    setShowModal(true);
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setShowModal(true);
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project? This will stop billing tracking.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'gcpProjects', projectId));
    } catch (error) {
      alert('Error deleting project: ' + error.message);
    }
  };

  const handleSyncCosts = async (project) => {
    setSyncing((prev) => ({ ...prev, [project.id]: true }));

    try {
      const syncCosts = httpsCallable(functions, 'syncGcpCosts');
      const result = await syncCosts({
        gcpProjectId: project.gcpProjectId,
        billingPeriod: new Date().toISOString().slice(0, 7),
      });

      alert(`Synced costs: $${result.data.totalCost?.toFixed(2) || '0.00'}`);
    } catch (error) {
      alert('Error syncing costs: ' + error.message);
    } finally {
      setSyncing((prev) => ({ ...prev, [project.id]: false }));
    }
  };

  const handleGenerateInvoice = async (project) => {
    if (!window.confirm(`Generate invoice for ${project.gcpProjectName}?`)) {
      return;
    }

    try {
      const generateInvoice = httpsCallable(functions, 'generateGcpInvoice');
      const result = await generateInvoice({
        gcpProjectId: project.id,
        billingPeriod: new Date().toISOString().slice(0, 7),
      });

      alert(`Invoice ${result.data.invoiceNumber} generated for $${result.data.totalAmount?.toFixed(2)}`);
    } catch (error) {
      alert('Error generating invoice: ' + error.message);
    }
  };

  const handleToggleAutoCharge = async (project) => {
    try {
      await updateDoc(doc(db, 'gcpProjects', project.id), {
        autoChargeEnabled: !project.autoChargeEnabled,
      });
    } catch (error) {
      alert('Error updating auto-charge: ' + error.message);
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    return client?.name || clientId;
  };

  const calculateBufferedCost = (project) => {
    const rawCost = project.estimatedCosts?.reduce((sum, c) => sum + c.rawCost, 0) || 0;
    let buffered = rawCost;

    if (project.marginConfig?.type === 'percentage' || project.marginConfig?.type === 'both') {
      buffered = rawCost * (1 + (project.marginConfig.percentageMarkup || 0));
    }
    if (project.marginConfig?.type === 'fixed' || project.marginConfig?.type === 'both') {
      buffered += project.marginConfig.fixedFee || 0;
    }

    // BigQuery actual costs (from sync)
    const actualCost = project.actualCosts?.totalRawCost || project.lastSyncedCost || null;
    // Suggested billing: actual cost + 20% markup
    const suggestedBilling = actualCost ? actualCost * 1.20 : null;

    return { rawCost, bufferedCost: buffered, margin: buffered - rawCost, actualCost, suggestedBilling };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getStatusBadge = (project) => {
    if (!project.billingEnabled) {
      return <span className="badge badge-disabled">Disabled</span>;
    }
    if (project.autoChargeEnabled) {
      return <span className="badge badge-auto">Auto-Charge</span>;
    }
    return <span className="badge badge-active">Active</span>;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading partner projects...</p>
        {user && <p style={{ fontSize: '0.8rem', color: '#666' }}>User: {user.uid}</p>}
      </div>
    );
  }

  if (error) {
    return (
      <div className="loading-container">
        <p style={{ color: '#ef4444' }}>Error: {error}</p>
        <p style={{ fontSize: '0.8rem', color: '#666' }}>User UID: {user?.uid}</p>
      </div>
    );
  }

  return (
    <div className="partner-projects">
      <div className="page-header">
        <div>
          <h1>Partner Projects</h1>
          <p className="page-subtitle">
            Manage GCP projects for automated cost tracking and billing
          </p>
        </div>
        <button className="btn btn-primary" onClick={handleAdd}>
          + Add Project
        </button>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-icon projects-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <div className="summary-content">
            <span className="summary-value">{projects.length}</span>
            <span className="summary-label">Projects</span>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon revenue-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </div>
          <div className="summary-content">
            <span className="summary-value">
              {formatCurrency(
                projects.reduce((sum, p) => sum + calculateBufferedCost(p).bufferedCost, 0)
              )}
            </span>
            <span className="summary-label">Est. Monthly Revenue</span>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon margin-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
              <polyline points="17 6 23 6 23 12" />
            </svg>
          </div>
          <div className="summary-content">
            <span className="summary-value">
              {formatCurrency(
                projects.reduce((sum, p) => sum + calculateBufferedCost(p).margin, 0)
              )}
            </span>
            <span className="summary-label">Est. Monthly Margin</span>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon autocharge-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
            </svg>
          </div>
          <div className="summary-content">
            <span className="summary-value">
              {projects.filter((p) => p.autoChargeEnabled).length}
            </span>
            <span className="summary-label">Auto-Charge Enabled</span>
          </div>
        </div>
      </div>

      {/* Projects Table */}
      {projects.length === 0 ? (
        <div className="empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
          </svg>
          <h3>No partner projects yet</h3>
          <p>Add your first GCP project to start tracking costs and billing automatically.</p>
          <button className="btn btn-primary" onClick={handleAdd}>
            + Add Project
          </button>
        </div>
      ) : (
        <div className="projects-table-container">
          <table className="projects-table">
            <thead>
              <tr>
                <th>Project</th>
                <th>Client</th>
                <th>GCP Project ID</th>
                <th>Actual (BQ)</th>
                <th>Suggested (+20%)</th>
                <th>Est. Monthly</th>
                <th>Margin</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => {
                const costs = calculateBufferedCost(project);
                const isExpanded = expandedProject === project.id;

                return (
                  <React.Fragment key={project.id}>
                    <tr
                      className={isExpanded ? 'expanded' : ''}
                      onClick={() => setExpandedProject(isExpanded ? null : project.id)}
                    >
                      <td>
                        <div className="project-name">
                          <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                          {project.gcpProjectName}
                        </div>
                      </td>
                      <td>{getClientName(project.clientId)}</td>
                      <td>
                        <code className="project-id">{project.gcpProjectId}</code>
                      </td>
                      <td className="amount actual-cost">
                        {costs.actualCost !== null ? formatCurrency(costs.actualCost) : <span className="no-data">—</span>}
                      </td>
                      <td className="amount suggested">
                        {costs.suggestedBilling !== null ? formatCurrency(costs.suggestedBilling) : <span className="no-data">—</span>}
                      </td>
                      <td className="amount">{formatCurrency(costs.bufferedCost)}</td>
                      <td className="amount margin">{formatCurrency(costs.margin)}</td>
                      <td>{getStatusBadge(project)}</td>
                      <td>
                        <div className="actions" onClick={(e) => e.stopPropagation()}>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleSyncCosts(project)}
                            disabled={syncing[project.id]}
                            title="Sync Costs"
                          >
                            {syncing[project.id] ? '...' : '↻'}
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleEdit(project)}
                            title="Edit"
                          >
                            ✎
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(project.id)}
                            title="Delete"
                          >
                            ✕
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="expanded-row">
                        <td colSpan="9">
                          <div className="project-details">
                            <div className="details-grid">
                              <div className="detail-section">
                                <h4>Billing Configuration</h4>
                                <div className="detail-row">
                                  <span>Billing Account:</span>
                                  <code>{project.billingAccountId || 'Not set'}</code>
                                </div>
                                <div className="detail-row">
                                  <span>Monthly Budget:</span>
                                  <span>{formatCurrency(project.monthlyBudget || 0)}</span>
                                </div>
                                <div className="detail-row">
                                  <span>Alert Thresholds:</span>
                                  <span>{project.alertThresholds?.join('%, ') || '50, 80, 100'}%</span>
                                </div>
                                <div className="detail-row">
                                  <span>Auto-Charge:</span>
                                  <label className="toggle">
                                    <input
                                      type="checkbox"
                                      checked={project.autoChargeEnabled || false}
                                      onChange={() => handleToggleAutoCharge(project)}
                                    />
                                    <span className="toggle-slider"></span>
                                  </label>
                                </div>
                              </div>

                              <div className="detail-section">
                                <h4>Margin Configuration</h4>
                                <div className="detail-row">
                                  <span>Type:</span>
                                  <span className="capitalize">{project.marginConfig?.type || 'fixed'}</span>
                                </div>
                                {(project.marginConfig?.type === 'percentage' ||
                                  project.marginConfig?.type === 'both') && (
                                  <div className="detail-row">
                                    <span>Markup:</span>
                                    <span>{((project.marginConfig?.percentageMarkup || 0) * 100).toFixed(0)}%</span>
                                  </div>
                                )}
                                {(project.marginConfig?.type === 'fixed' ||
                                  project.marginConfig?.type === 'both') && (
                                  <div className="detail-row">
                                    <span>Fixed Fee:</span>
                                    <span>{formatCurrency(project.marginConfig?.fixedFee || 0)}</span>
                                  </div>
                                )}
                                <div className="detail-row">
                                  <span>Description:</span>
                                  <span>{project.marginConfig?.fixedFeeDescription || 'Operations fee'}</span>
                                </div>
                              </div>

                              <div className="detail-section">
                                <h4>Estimated Costs</h4>
                                {project.estimatedCosts?.map((cost, idx) => (
                                  <div className="detail-row" key={idx}>
                                    <span>{cost.service}:</span>
                                    <span>{formatCurrency(cost.rawCost)}</span>
                                  </div>
                                ))}
                                <div className="detail-row total">
                                  <span>Raw Total:</span>
                                  <span>{formatCurrency(costs.rawCost)}</span>
                                </div>
                                <div className="detail-row total">
                                  <span>Billed Total:</span>
                                  <span>{formatCurrency(costs.bufferedCost)}</span>
                                </div>
                              </div>

                              <div className="detail-section">
                                <h4>Quick Actions</h4>
                                <button
                                  className="btn btn-primary btn-block"
                                  onClick={() => handleGenerateInvoice(project)}
                                >
                                  Generate Invoice
                                </button>
                                <button
                                  className="btn btn-secondary btn-block"
                                  onClick={() => handleSyncCosts(project)}
                                  disabled={syncing[project.id]}
                                >
                                  {syncing[project.id] ? 'Syncing...' : 'Sync Costs Now'}
                                </button>
                                {project.clientId && (
                                  <a
                                    href={`/clients`}
                                    className="btn btn-secondary btn-block"
                                  >
                                    View Client
                                  </a>
                                )}
                              </div>
                            </div>

                            <div className="portal-info">
                              <h4>Partner Portal</h4>
                              <p>Share this link with the client to view invoices and manage payments:</p>
                              <code className="portal-url">
                                https://billing.fusionict.com/portal/[access-token]
                              </code>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <PartnerProjectModal
          project={editingProject}
          clients={clients}
          onClose={() => {
            setShowModal(false);
            setEditingProject(null);
          }}
        />
      )}
    </div>
  );
};

export default PartnerProjects;
